import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const checkStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsStandalone(checkStandalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if user hasn't dismissed before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed && !checkStandalone) {
        setTimeout(() => setShowPrompt(true), 3000); // Show after 3 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS prompt if not dismissed and not installed
    if (iOS && !checkStandalone) {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 5000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to install prompt: ${outcome}`);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (isStandalone || !showPrompt) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-96 bg-white rounded-xl shadow-2xl border border-stone-200 p-4 z-50 animate-in slide-in-from-bottom-5"
      data-testid="pwa-install-prompt"
    >
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-stone-400 hover:text-stone-600"
        aria-label="Dismiss"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
          <Smartphone className="w-6 h-6 text-primary" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#2D2D2D] mb-1">Install Calm Pathways</h3>
          <p className="text-sm text-[#57534E] mb-3">
            {isIOS 
              ? "Tap the share button, then 'Add to Home Screen' for quick access."
              : "Install the app for quick access and offline support."}
          </p>
          
          {!isIOS && deferredPrompt && (
            <Button 
              onClick={handleInstall}
              className="w-full bg-primary hover:bg-primary/90"
              data-testid="pwa-install-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Install App
            </Button>
          )}
          
          {isIOS && (
            <div className="text-xs text-[#57534E] bg-stone-50 rounded-lg p-2">
              <p className="flex items-center gap-1">
                <span>1. Tap</span>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 16a1 1 0 01-.707-.293l-4-4a1 1 0 111.414-1.414L12 13.586l3.293-3.293a1 1 0 111.414 1.414l-4 4A1 1 0 0112 16z"/>
                  <path d="M12 4a1 1 0 011 1v7a1 1 0 11-2 0V5a1 1 0 011-1z"/>
                  <path d="M4 14a1 1 0 011 1v2a1 1 0 001 1h12a1 1 0 001-1v-2a1 1 0 112 0v2a3 3 0 01-3 3H6a3 3 0 01-3-3v-2a1 1 0 011-1z"/>
                </svg>
                <span>Share</span>
              </p>
              <p>2. Select "Add to Home Screen"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
